// Voice input for the chat composer — wired to the mic button +
// kebab language menu inside app/views/chat_pages/_composer.html.erb.
// Used by BOTH the /chat page and the bubble drawer (the composer
// partial is shared).
//
// Architecture:
//   click mic → POST /transcribe/credentials → STS GetFederationToken
//     → AudioContext + AudioWorklet (Float32 → 16k Int16 PCM)
//     → @aws-sdk/client-transcribe-streaming opens WebSocket to AWS
//     → async-iterate TranscriptResultStream
//     → partial results replace the textarea tail; finals commit + space
//   click mic again (or chat clicks Send) → stop, release mic
//
// Stealth feature — see docs/chat/guardrails.md § 10. No user-visible
// copy here narrates "voice / transcribe / AWS / dictation" beyond
// the mic button's aria-label="Dictate" + title="Dictate".

import { Controller } from "@hotwired/stimulus"
import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
} from "@aws-sdk/client-transcribe-streaming"

const LANG_STORAGE_KEY = "autoboost_transcribe_lang"
const DEFAULT_LANG = "en-US"

export default class extends Controller {
  static targets = [
    "micButton",   // the mic <button> — toggles is-loading / is-recording classes
    "micIcon",     // static mic glyph; hidden while loading or recording (CSS)
    "spinner",     // instant rotating spinner shown while creds/mic/WS warm up
    "waves",       // 5-bar waveform overlay; shown while recording
    "stopButton",  // visible Stop pill shown only while recording
    "langMenu",    // the kebab dropdown <div>
    "langToggle",  // the EN|FR segmented pill — data-lang flips to slide indicator
  ]
  static values = {
    credentialsUrl: String,
    workletUrl: String,
  }

  // ---------- lifecycle ----------

  connect() {
    this.recording = false
    this.client = null
    this.audioContext = null
    this.workletNode = null
    this.mediaStream = null
    this.audioQueue = []
    this.queueResolver = null
    this.partialStart = 0     // cursor: where the next partial replaces in textarea
    this.langMenuOpen = false

    this.currentLang = (typeof window !== "undefined" && window.localStorage)
      ? (window.localStorage.getItem(LANG_STORAGE_KEY) || DEFAULT_LANG)
      : DEFAULT_LANG
    this.refreshLangLabel()

    console.log(`[transcribe] connect: credentialsUrl=${this.credentialsUrlValue} workletUrl=${this.workletUrlValue} lang=${this.currentLang}`)
  }

  disconnect() {
    // Belt-and-braces cleanup — if the Stimulus element is removed
    // mid-recording, release the mic + close the AudioContext.
    this.stop().catch(() => {})
  }

  // ---------- public actions ----------

  // click->transcribe#toggle on the mic button.
  toggle(event) {
    event?.preventDefault?.()
    if (this.recording) {
      this.stop().catch((e) => console.error("[transcribe] stop error:", e))
    } else {
      // Show the spinner BEFORE any async work — getUserMedia +
      // STS fetch + AudioContext spin-up takes ~1-2s, the button
      // should never look "stuck" with the mic icon during that
      // window.
      this.beginLoadingUiState()
      this.start().catch((e) => {
        console.error("[transcribe] start error:", e)
        this.endRecordingUiState()
      })
    }
  }

  // chat:before-send@window listener. Auto-flush so the latest partial
  // is committed to the textarea BEFORE chat-panel/chat-bubble POSTs
  // the message. Synchronous from the textarea's perspective — the
  // latest applied partial is already in textarea.value when this
  // returns; the WebSocket cleanup happens async after.
  beforeSend(_event) {
    if (!this.recording) return
    console.log("[transcribe] beforeSend: auto-flushing partial before /chats POST")
    this.recording = false
    this.endRecordingUiState()
    // Best-effort async cleanup of the network resources.
    this.releaseAudioPipeline()
    this.closeClient().catch(() => {})
  }

  // click->transcribe#toggleLangMenu on the kebab button.
  toggleLangMenu(event) {
    event?.preventDefault?.()
    this.langMenuOpen = !this.langMenuOpen
    if (this.hasLangMenuTarget) {
      this.langMenuTarget.classList.toggle("hidden", !this.langMenuOpen)
    }
  }

  // Click outside the menu closes it. Bound via data-action on the
  // wrapper to keydown.esc and click@window.
  closeLangMenu(event) {
    if (!this.langMenuOpen) return
    // Don't close when clicking inside the menu itself.
    if (event && this.hasLangMenuTarget && this.langMenuTarget.contains(event.target)) return
    this.langMenuOpen = false
    if (this.hasLangMenuTarget) this.langMenuTarget.classList.add("hidden")
  }

  // click->transcribe#switchLanguage on the menu item.
  switchLanguage(event) {
    event?.preventDefault?.()
    this.currentLang = this.currentLang === "en-US" ? "fr-FR" : "en-US"
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LANG_STORAGE_KEY, this.currentLang)
    }
    this.refreshLangLabel()
    this.closeLangMenu()
    console.log(`[transcribe] language switched to ${this.currentLang}`)

    // If currently recording, restart so the new language takes effect.
    if (this.recording) {
      this.stop().then(() => this.start()).catch((e) => console.error("[transcribe] restart error:", e))
    }
  }

  // ---------- internals ----------

  refreshLangLabel() {
    if (!this.hasLangToggleTarget) return
    // The toggle's data-lang attribute drives the CSS slide animation
    // (transform: translateX on the slider span). aria-checked
    // mirrors it for screen readers — "fr" = checked, "en" = unchecked.
    const tag = this.currentLang === "fr-FR" ? "fr" : "en"
    this.langToggleTarget.setAttribute("data-lang", tag)
    this.langToggleTarget.setAttribute("aria-checked", tag === "fr" ? "true" : "false")
  }

  async start() {
    if (this.recording) return

    console.log("[transcribe] start: fetching credentials")
    const creds = await this.fetchCredentials()

    console.log("[transcribe] start: requesting microphone")
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    })

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    await this.audioContext.audioWorklet.addModule(this.workletUrlValue)
    const source = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-recorder-processor")
    source.connect(this.workletNode)
    // Intentionally NOT connecting to destination — avoids feedback.

    this.audioQueue = []
    this.queueResolver = null
    this.workletNode.port.onmessage = (event) => {
      const buf = new Uint8Array(event.data)
      if (this.queueResolver) {
        const resolve = this.queueResolver
        this.queueResolver = null
        resolve({ value: buf, done: false })
      } else {
        this.audioQueue.push(buf)
      }
    }

    // Anchor where the next partial will start replacing. Anything
    // typed by the user before clicking mic stays; voice gets appended.
    const textarea = this.findTextarea()
    this.partialStart = textarea ? textarea.value.length : 0
    if (textarea && this.partialStart > 0 && !textarea.value.endsWith(" ")) {
      // Add a separator so voice doesn't collide with prior typed text.
      textarea.value = textarea.value + " "
      this.partialStart = textarea.value.length
    }

    const audioStream = this.buildAudioGenerator()

    this.client = new TranscribeStreamingClient({
      region: creds.region,
      credentials: {
        accessKeyId:     creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken:    creds.sessionToken,
      },
    })

    const command = new StartStreamTranscriptionCommand({
      LanguageCode: this.currentLang,                 // "en-US" or "fr-FR"
      MediaEncoding: "pcm",
      MediaSampleRateHertz: 16000,
      EnablePartialResultsStabilization: true,
      PartialResultsStability: "high",
      AudioStream: audioStream,
    })

    console.log(`[transcribe] start: opening Transcribe stream lang=${this.currentLang} region=${creds.region}`)
    this.recording = true
    this.beginRecordingUiState()
    let response
    try {
      response = await this.client.send(command)
    } catch (e) {
      console.error("[transcribe] StartStreamTranscription failed:", e)
      this.recording = false
      this.endRecordingUiState()
      this.releaseAudioPipeline()
      return
    }

    // Background iteration. Loop ends when this.recording flips false
    // (the async-generator yields done, the SDK closes the WS).
    this.iterateResults(response).catch((e) => {
      console.error("[transcribe] iterateResults error:", e)
      this.recording = false
      this.endRecordingUiState()
    })
  }

  buildAudioGenerator() {
    const self = this
    return (async function* () {
      while (self.recording) {
        let next
        if (self.audioQueue.length > 0) {
          next = { value: self.audioQueue.shift(), done: false }
        } else {
          next = await new Promise((resolve) => { self.queueResolver = resolve })
        }
        if (next.done) return
        yield { AudioEvent: { AudioChunk: next.value } }
      }
    })()
  }

  async iterateResults(response) {
    for await (const event of response.TranscriptResultStream) {
      const results = event.TranscriptEvent?.Transcript?.Results ?? []
      for (const r of results) {
        const transcript = r.Alternatives?.[0]?.Transcript ?? ""
        this.applyTranscript({ partial: !!r.IsPartial, text: transcript })
      }
    }
    console.log("[transcribe] iterateResults: stream ended")
  }

  applyTranscript({ partial, text }) {
    const textarea = this.findTextarea()
    if (!textarea) return

    const before = textarea.value.slice(0, this.partialStart)
    if (partial) {
      textarea.value = before + text
    } else {
      // Final — commit and advance the cursor anchor past it (+space).
      const committed = text + " "
      textarea.value = before + committed
      this.partialStart = before.length + committed.length
    }
    // Fire input event so chat-panel#autoGrow / chat-bubble#autoGrow run.
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    // Keep the textarea scrolled to the bottom while we type into it.
    textarea.scrollTop = textarea.scrollHeight
  }

  async stop() {
    if (!this.recording && !this.audioContext) return
    console.log("[transcribe] stop")
    this.recording = false
    if (this.queueResolver) {
      const r = this.queueResolver
      this.queueResolver = null
      r({ value: null, done: true })
    }
    this.releaseAudioPipeline()
    await this.closeClient()
    this.endRecordingUiState()
  }

  releaseAudioPipeline() {
    try { this.workletNode?.disconnect() } catch (_) {}
    try { this.mediaStream?.getTracks().forEach((t) => t.stop()) } catch (_) {}
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
    }
    this.workletNode = null
    this.mediaStream = null
  }

  async closeClient() {
    if (!this.client) return
    try { this.client.destroy() } catch (_) {}
    this.client = null
  }

  // Three UI states for the mic button, controlled by two class
  // toggles on the button itself:
  //   idle      → no class            → mic icon visible
  //   loading   → .is-loading         → spinner visible (warm-up)
  //   recording → .is-recording       → waveform + Stop pill visible
  // The CSS opacity transitions handle the cross-fade.
  beginLoadingUiState() {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.classList.add("is-loading")
      this.micButtonTarget.classList.remove("is-recording")
    }
    if (this.hasStopButtonTarget) this.stopButtonTarget.classList.add("hidden")
  }

  beginRecordingUiState() {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.classList.remove("is-loading")
      this.micButtonTarget.classList.add("is-recording")
      this.micButtonTarget.setAttribute("aria-pressed", "true")
    }
    // Waveform fades in via CSS once .is-recording is on the parent.
    // The Stop pill is hidden by default — reveal it now.
    if (this.hasStopButtonTarget) this.stopButtonTarget.classList.remove("hidden")
  }

  endRecordingUiState() {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.classList.remove("is-loading")
      this.micButtonTarget.classList.remove("is-recording")
      this.micButtonTarget.setAttribute("aria-pressed", "false")
    }
    if (this.hasStopButtonTarget) this.stopButtonTarget.classList.add("hidden")
  }

  findTextarea() {
    // The textarea lives in the composer partial alongside the mic.
    // It carries data-chat-panel-target="input" and/or
    // data-chat-bubble-target="input"; either way it's the single
    // textarea inside .chat-composer-wrapper.
    return this.element.querySelector("textarea")
  }

  async fetchCredentials() {
    const res = await fetch(this.credentialsUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
    })
    if (!res.ok) {
      throw new Error(`Credentials fetch failed: ${res.status}`)
    }
    return res.json()
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
